import { createContext, type RefObject, use, useEffect, useEffectEvent } from "react"
import {
  type Candidate,
  type Landing,
  type Pending,
  admits,
  byStanding,
  isActivatable,
  isEditable,
  step,
} from "./dispatch.ts"
import { type ShortcutId, shortcutOf, shortcuts } from "./registry.ts"

/*
 * The one keyboard listener: every mounted `useShortcut` registers here, and each key press goes
 * to the best of those that match it. What the press landed on is read in the capture phase, before
 * anything handles it, because an overlay that closes on Esc may be gone from the DOM by the time
 * the press bubbles back up to the window, and the page's own Esc would then take it too.
 *
 * A shortcut's handler may return `false` to pass: the press goes to the next that matches, and
 * does whatever it would have done without shortcuts — as Enter does when no row is highlighted.
 */

/** What a shortcut does. Returning `false` passes the press on. */
export type ShortcutHandler = (event: KeyboardEvent) => void | false

type Registration = {
  id: ShortcutId
  run: ShortcutHandler
  within: RefObject<Element | null> | undefined
  order: number
  fallback: boolean
}

const registrations = new Set<Registration>()
const landings = new WeakMap<KeyboardEvent, Landing>()
let registered = 0
let pending: Pending | undefined
let listening = false

// What counts as open over the page: Base UI portals every dialog, popover, menu and listbox, and
// marks one on its way out, so an inline demo or a closing popup doesn't hold the keys.
const overlays =
  "[data-base-ui-portal] :is([role=dialog], [role=alertdialog], [role=menu], [role=listbox])" +
  ":not([data-closed]):not([data-ending-style])"

function land(event: KeyboardEvent) {
  const target = event.target instanceof Element ? event.target : null
  landings.set(event, {
    editable: target instanceof HTMLElement && isEditable(target),
    activatable: target instanceof HTMLElement && isActivatable(target),
    overlayOpen: document.querySelector(overlays) !== null,
  })
}

function dispatch(event: KeyboardEvent) {
  const landing = landings.get(event)
  if (!landing || event.defaultPrevented || event.isComposing) {
    pending = undefined
    return
  }
  const target = event.target instanceof Node ? event.target : null
  // Best first, by `byStanding`: a fallback such as the shell's `/` comes after every other,
  // however late it mounted, so the palette opens only where no search field has bound it.
  const candidates: Candidate<Registration>[] = [...registrations]
    .map((registration) => ({
      registration,
      // Undefined for an unscoped binding, which `admits` tells apart from one scoped elsewhere.
      inside:
        registration.within && Boolean(target && registration.within.current?.contains(target)),
    }))
    .filter(({ registration, inside }) =>
      admits(shortcutOf(registration.id), event, landing, inside),
    )
    .toSorted((a, b) =>
      byStanding(
        { ...a.registration, inside: a.inside === true },
        { ...b.registration, inside: b.inside === true },
      ),
    )
    .map(({ registration }) => ({ keys: shortcuts[registration.id].keys, value: registration }))

  const next = step(candidates, event, pending, event.timeStamp)
  pending = next.pending
  for (const registration of next.fire) {
    if (registration.run(event) === false) continue
    event.preventDefault()
    return
  }
}

function listen() {
  if (listening) return
  window.addEventListener("keydown", land, true)
  window.addEventListener("keydown", dispatch)
  listening = true
}

type Options = {
  /** Whether the shortcut is on: off for a Member who can't do what it does. On by default. */
  enabled?: boolean
  /**
   * The element it belongs to, when it works only there: a form's ⌘↵, a focused row's Alt+↑.
   * Unlike a page's shortcut, it still fires while its element sits in an open dialog.
   */
  within?: RefObject<Element | null>
  /**
   * It yields to every other binding of its keys, whichever mounted first, as the shell's `/`
   * yields to a page's search field. Without it the latest mounted wins, and a page that mounts
   * before the shell on a fresh load would lose its keys to it.
   */
  fallback?: boolean
}

/**
 * The element every shortcut bound inside it belongs to, unless it names its own `within`: the
 * Ticket peek's, so the ticket page's shortcuts work in the peek as they do on the page, while the
 * page beneath it, as under any overlay, stands aside.
 */
export const ShortcutScope = createContext<RefObject<Element | null> | undefined>(undefined)

/**
 * Binds a registry shortcut to what it does while the calling component is mounted. The handler
 * may change every render; the shortcut always runs the latest.
 */
export function useShortcut(id: ShortcutId, handler: ShortcutHandler, options: Options = {}) {
  useShortcuts({ [id]: handler }, options)
}

/**
 * Binds several registry shortcuts at once, as the shell binds every "Go to" chord. Which ones are
 * bound is read from the handlers' ids; the handlers may change every render.
 */
export function useShortcuts(
  handlers: Partial<Record<ShortcutId, ShortcutHandler>>,
  { enabled = true, within: own, fallback = false }: Options = {},
) {
  const scope = use(ShortcutScope)
  const within = own ?? scope
  const run = useEffectEvent((id: ShortcutId, event: KeyboardEvent) => handlers[id]?.(event))
  const ids = (Object.keys(handlers) as ShortcutId[]).toSorted().join(" ")

  useEffect(() => {
    if (!enabled || !ids) return
    const mounted = (ids.split(" ") as ShortcutId[]).map((id): Registration => ({
      id,
      run: (event) => run(id, event),
      within,
      order: ++registered,
      fallback,
    }))
    for (const registration of mounted) registrations.add(registration)
    listen()
    return () => {
      for (const registration of mounted) registrations.delete(registration)
    }
  }, [ids, enabled, within, fallback])
}
